import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getWeeklyPlan, getTodayPlan, completeSchedule, ignoreSchedule } from '../lib/api';
import { SKETCH_PATTERNS } from '../lib/images';
import { Link } from 'react-router-dom';
import {
  Loader2, Calendar, Check, X, ChevronRight, Sun, Coffee,
  Moon, Sunset, Clock, Tv, Music, BookOpen, Dumbbell,
  Play, Star, Sparkles
} from 'lucide-react';

const periodIcons = { Morning: Sun, Afternoon: Sunset, Evening: Moon, Night: Moon };
const periodColors = { Morning: '#FFD93D', Afternoon: '#E2725B', Evening: '#9B59B6', Night: '#4D96FF' };

export default function WeeklyPlanPage() {
  const { deviceId, user } = useAuth();
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [todayPlan, setTodayPlan] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  const load = useCallback(async () => {
    try {
      const [weekRes, todayRes] = await Promise.allSettled([
        getWeeklyPlan(deviceId),
        getTodayPlan(deviceId, user?.id || user?._id || user?.user_id),
      ]);
      if (weekRes.status === 'fulfilled') {
        setWeeklyPlan(weekRes.value.data);
        const today = weekRes.value.data?.days?.find(d => d.is_today);
        if (today) setSelectedDay(today.date);
      }
      if (todayRes.status === 'fulfilled') setTodayPlan(todayRes.value.data);
    } catch {}
    setLoading(false);
  }, [deviceId, user]);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async (scheduleId) => {
    setActionLoading(prev => ({ ...prev, [scheduleId]: 'completing' }));
    try {
      await completeSchedule(scheduleId, { device_id: deviceId });
      await load();
    } catch {}
    setActionLoading(prev => ({ ...prev, [scheduleId]: null }));
  };

  const handleIgnore = async (scheduleId) => {
    setActionLoading(prev => ({ ...prev, [scheduleId]: 'ignoring' }));
    try {
      await ignoreSchedule(scheduleId, { device_id: deviceId });
      await load();
    } catch {}
    setActionLoading(prev => ({ ...prev, [scheduleId]: null }));
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );

  const days = weeklyPlan?.days || [];
  const selectedDayData = days.find(d => d.date === selectedDay);
  const grouped = todayPlan?.grouped_activities || {};
  const progress = todayPlan?.progress || { completed: 0, total: 0, percent: 0 };
  const entertainment = todayPlan?.entertainment;
  const hasActivities = days.some(d => d.total > 0);

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 max-w-5xl mx-auto" data-testid="weekly-plan-page">
      {/* Header */}
      <div className="rounded-lg overflow-hidden mb-8 relative h-36 keep-light-theme"
        style={{ background: 'linear-gradient(135deg, #F2EFE9 0%, #E5E1D8 50%, #D4CBB3 100%)' }}>
        <div className="absolute inset-0 opacity-12"
          style={{ backgroundImage: `url("${SKETCH_PATTERNS.leafBranch}")`, backgroundSize: '500px', backgroundPosition: 'right center', backgroundRepeat: 'no-repeat' }} />
        <div className="absolute inset-0 flex items-center px-8">
          <div>
            <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Weekly Plan</h1>
            <p className="text-sm" style={{ color: 'var(--muted-fg)' }}>
              Week of {weeklyPlan?.week_start ? new Date(weeklyPlan.week_start).toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : 'This week'}
            </p>
          </div>
        </div>
      </div>

      {/* Week Day Selector */}
      <div className="grid grid-cols-7 gap-1.5 sm:gap-2 mb-6 sm:mb-8" data-testid="week-day-selector">
        {days.map((day, i) => {
          const isSelected = selectedDay === day.date;
          const pct = day.total > 0 ? Math.round((day.completed / day.total) * 100) : 0;
          return (
            <button key={day.date} onClick={() => setSelectedDay(day.date)}
              className="rounded-lg p-3 border text-center transition-all hover:-translate-y-0.5"
              style={{
                background: isSelected ? 'var(--primary)' : day.is_today ? 'var(--primary)' + '08' : 'var(--surface)',
                color: isSelected ? 'var(--primary-fg)' : 'var(--text)',
                borderColor: isSelected ? 'var(--primary)' : day.is_today ? 'var(--primary)' + '40' : 'var(--border)',
                opacity: day.is_past && !isSelected ? 0.6 : 1,
              }}
              data-testid={`day-selector-${day.day_short.toLowerCase()}`}>
              <p className="text-xs font-medium mb-1">{day.day_short}</p>
              <p className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>
                {new Date(day.date + 'T12:00:00').getDate()}
              </p>
              {day.total > 0 && (
                <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: isSelected ? 'rgba(255,255,255,0.3)' : 'var(--muted)' }}>
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: isSelected ? 'white' : 'var(--primary)' }} />
                </div>
              )}
              {day.total > 0 && (
                <p className="text-xs mt-1" style={{ opacity: 0.7 }}>{day.completed}/{day.total}</p>
              )}
            </button>
          );
        })}
      </div>

      {/* Today's Progress (only for today) */}
      {selectedDayData?.is_today && progress.total > 0 && (
        <div className="rounded-lg p-5 border mb-6 animate-fade-in"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="today-progress">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Today's Progress</h3>
            <span className="text-sm font-bold" style={{ color: 'var(--primary)' }}>{progress.percent}%</span>
          </div>
          <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--muted)' }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress.percent}%`, background: 'var(--primary)' }} />
          </div>
          <p className="text-xs mt-2" style={{ color: 'var(--muted-fg)' }}>{progress.completed} of {progress.total} activities completed</p>
        </div>
      )}

      {/* Day Activities */}
      {selectedDayData && (
        <div className="space-y-6 animate-fade-in" data-testid="day-activities">
          {selectedDayData.activities.length > 0 ? (
            <>
              {/* Group by time period if today */}
              {selectedDayData.is_today && Object.keys(grouped).some(k => grouped[k]?.length > 0) ? (
                Object.entries(grouped).map(([period, activities]) => {
                  if (!activities || activities.length === 0) return null;
                  const PeriodIcon = periodIcons[period] || Sun;
                  const periodColor = periodColors[period] || 'var(--primary)';
                  return (
                    <div key={period} data-testid={`period-${period.toLowerCase()}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <PeriodIcon className="w-4 h-4" style={{ color: periodColor }} />
                        <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{period}</h3>
                        <span className="text-xs" style={{ color: 'var(--muted-fg)' }}>
                          {activities.filter(a => a.status === 'completed').length}/{activities.length}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {activities.map(activity => (
                          <ActivityItem key={activity.schedule_id} activity={activity}
                            onComplete={handleComplete} onIgnore={handleIgnore}
                            actionLoading={actionLoading} />
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div>
                  <h3 className="font-bold text-sm mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
                    {selectedDayData.day_name}'s Activities
                  </h3>
                  <div className="space-y-2">
                    {selectedDayData.activities.map(activity => (
                      <ActivityItem key={activity.schedule_id} activity={activity}
                        onComplete={handleComplete} onIgnore={handleIgnore}
                        actionLoading={actionLoading} />
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <EmptyState day={selectedDayData} />
          )}
        </div>
      )}

      {/* Entertainment (today only) */}
      {selectedDayData?.is_today && entertainment && (
        <div className="rounded-lg p-5 border mt-6 animate-fade-in"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="entertainment-section">
          <div className="flex items-center gap-2 mb-4">
            <Tv className="w-5 h-5" style={{ color: '#3498DB' }} />
            <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Wind Down Picks</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {entertainment.movie && (
              <EntertainmentCard icon={Play} label="Movie" title={entertainment.movie.title}
                subtitle={entertainment.movie.platform} />
            )}
            {entertainment.show && (
              <EntertainmentCard icon={Tv} label="Show" title={entertainment.show.title}
                subtitle={entertainment.show.platform} />
            )}
            {entertainment.podcast && (
              <EntertainmentCard icon={Music} label="Podcast" title={entertainment.podcast.title}
                subtitle={entertainment.podcast.host} />
            )}
            {entertainment.spotify_playlist && (
              <EntertainmentCard icon={Music} label="Playlist" title={entertainment.spotify_playlist.name}
                subtitle={entertainment.spotify_playlist.mood} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityItem({ activity, onComplete, onIgnore, actionLoading }) {
  const isCompleted = activity.status === 'completed';
  const isIgnored = activity.status === 'ignored';
  const isLoading = actionLoading[activity.schedule_id];

  return (
    <div className="rounded-lg p-4 border flex items-center gap-3 transition-all"
      style={{
        background: isCompleted ? 'var(--primary)' + '08' : isIgnored ? 'var(--muted)' + '50' : 'var(--surface)',
        borderColor: isCompleted ? 'var(--primary)' + '40' : 'var(--border)',
        opacity: isIgnored ? 0.5 : 1,
      }}
      data-testid={`activity-${activity.schedule_id}`}>
      {/* Complete toggle */}
      <button onClick={() => !isCompleted && onComplete(activity.schedule_id)} disabled={isCompleted || !!isLoading}
        className="w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 transition-all disabled:cursor-default"
        style={{
          borderColor: isCompleted ? 'var(--primary)' : 'var(--border)',
          background: isCompleted ? 'var(--primary)' : 'transparent',
        }}
        data-testid={`complete-${activity.schedule_id}`}>
        {isLoading === 'completing' ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--muted-fg)' }} />
        ) : isCompleted ? (
          <Check className="w-4 h-4 text-white" />
        ) : null}
      </button>

      {/* Activity info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate" style={{
          color: 'var(--text)',
          textDecoration: isCompleted ? 'line-through' : isIgnored ? 'line-through' : 'none',
          opacity: isCompleted || isIgnored ? 0.6 : 1,
        }}>{activity.title}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {activity.time && (
            <span className="text-xs flex items-center gap-1" style={{ color: 'var(--muted-fg)' }}>
              <Clock className="w-3 h-3" /> {activity.time}
            </span>
          )}
          {activity.program_id && (
            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--muted)', color: 'var(--muted-fg)' }}>
              {activity.program_name || activity.program_id}
            </span>
          )}
        </div>
      </div>

      {/* Ignore button */}
      {!isCompleted && !isIgnored && (
        <button onClick={() => onIgnore(activity.schedule_id)} disabled={!!isLoading}
          className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all hover:opacity-70"
          style={{ color: 'var(--muted-fg)' }}
          data-testid={`ignore-${activity.schedule_id}`}>
          {isLoading === 'ignoring' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <X className="w-4 h-4" />
          )}
        </button>
      )}

      {/* Status badges */}
      {isCompleted && (
        <span className="text-xs font-medium shrink-0" style={{ color: 'var(--primary)' }}>Done</span>
      )}
      {isIgnored && (
        <span className="text-xs font-medium shrink-0" style={{ color: 'var(--muted-fg)' }}>Skipped</span>
      )}
    </div>
  );
}

function EntertainmentCard({ icon: Icon, label, title, subtitle }) {
  return (
    <div className="rounded-lg p-3" style={{ background: 'var(--bg)' }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color: 'var(--primary)' }} />
        <span className="text-xs font-semibold" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>{label}</span>
      </div>
      <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{title}</p>
      <p className="text-xs truncate" style={{ color: 'var(--muted-fg)' }}>{subtitle}</p>
    </div>
  );
}

function EmptyState({ day }) {
  return (
    <div className="rounded-lg p-8 border text-center animate-fade-in"
      style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="empty-day">
      <Calendar className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--muted)' }} />
      <h3 className="font-bold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
        No activities for {day.day_name}
      </h3>
      <p className="text-sm mb-5" style={{ color: 'var(--muted-fg)' }}>
        Enroll in a wellness program to fill your schedule with daily activities.
      </p>
      <Link to="/programs" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all hover:opacity-90"
        style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }} data-testid="browse-programs-cta">
        <Dumbbell className="w-4 h-4" /> Browse Programs
      </Link>
    </div>
  );
}
